import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, List, Tag, Button, Progress, Popconfirm, Typography, Empty, Alert, App } from 'antd';
import repairDataLookupApi, { LookupJob } from '../../services/api/repairDataLookupApi';

const { Text } = Typography;

/**
 * The vendor lookup queue.
 *
 * A lookup takes minutes and the shop has ONE vendor seat, so they run one at a
 * time. Three things the owner asked for and this exists to deliver:
 *
 *  - staff can see PROGRESS on each job, not a spinner;
 *  - staff can see the QUEUE, so "nothing is happening" is distinguishable from
 *    "you are third in line";
 *  - staff can REMOVE a job — a mistaken click must not cost the wait for a job
 *    nobody needs.
 *
 * Everything shown here is honest about not knowing. There is no timer-driven
 * percentage: a bar that sits at 90% for ten minutes teaches people to ignore it.
 */

/** Terminal states stop polling — there is nothing left to watch. */
const isDone = (s: LookupJob['status']) =>
  s === 'succeeded' || s === 'failed' || s === 'cancelled' || s === 'needs_choice';

/**
 * When it ran, in the words someone would use out loud.
 *
 * The server sends SQLite's `YYYY-MM-DD HH:MM:SS` in UTC with no zone marker, so the
 * `Z` has to be added or every time reads hours off in the shop's own timezone.
 */
const whenRan = (at: string): string => {
  const t = new Date(at.includes('T') ? at : `${at.replace(' ', 'T')}Z`);
  if (Number.isNaN(t.getTime())) return at;
  const mins = Math.round((Date.now() - t.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  if (mins < 24 * 60) return t.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return t.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

/** How long it held the seat. Minutes matter here; milliseconds do not. */
const howLong = (seconds: number): string => {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s ? `${m}m ${s}s` : `${m}m`;
};

const STATUS: Record<LookupJob['status'], { color: string; label: string }> = {
  pending: { color: 'default', label: 'Waiting' },
  running: { color: 'processing', label: 'Looking up' },
  succeeded: { color: 'success', label: 'Done' },
  failed: { color: 'error', label: 'Failed' },
  cancelled: { color: 'default', label: 'Cancelled' },
  cancelling: { color: 'warning', label: 'Stopping' },
  // Not a failure and not "done". The lookup worked; it found a decision only a
  // person can make, and it is holding nothing while it waits.
  needs_choice: { color: 'warning', label: 'Needs a choice' },
};

/**
 * How long this job is likely to take, in words.
 *
 * Deliberately vague and never a countdown. The estimate is measured from the
 * shop's own recent lookups, so it is a reasonable guess and not a commitment; a
 * countdown that stalls is worse than a range.
 */
const waitText = (job: LookupJob): string | null => {
  if (isDone(job.status)) return null;
  if (job.etaMinutes == null) return job.position > 0 ? `${job.position} ahead` : null;
  const mins = Math.max(1, Math.round(job.etaMinutes));
  const ahead = job.position > 0 ? `${job.position} ahead — ` : '';
  return `${ahead}about ${mins} min`;
};

export interface LookupQueuePanelProps {
  /** Called when a job finishes successfully, so the screen can reload its lines. */
  onCompleted?: (job: LookupJob) => void;
  /** Rendered inside an existing card. */
  bare?: boolean;
  /**
   * The document this panel is being shown on.
   *
   * The queue is the whole shop's — deliberately, because with one vendor seat a
   * job on someone else's estimate is exactly why yours has not started. But a
   * question ("which vehicle did you mean: 2WD or 4WD?") belongs to ONE job, and
   * answering it changes the parts on THAT document. Shown unlabelled on a
   * different estimate, the options describe a truck that is not the one on the
   * screen, and the obvious reading — "this is about the vehicle I am looking
   * at" — is wrong. So a job from another document says whose it is, and its
   * answer buttons are not offered here.
   */
  documentType?: 'estimate' | 'work_order' | null;
  documentId?: number | null;
}

const DOCUMENT_WORD: Record<'estimate' | 'work_order', string> = {
  estimate: 'estimate',
  work_order: 'work order',
};

export const LookupQueuePanel: React.FC<LookupQueuePanelProps> = ({
  onCompleted,
  bare,
  documentType,
  documentId,
}) => {
  const { message } = App.useApp();
  const [jobs, setJobs] = useState<LookupJob[]>([]);
  const [failed, setFailed] = useState(false);
  // Which jobs have already been announced, so a completed job fires onCompleted
  // once rather than on every poll that still lists it.
  const announced = useRef<Set<string>>(new Set());

  /**
   * The completion callback, held in a ref rather than closed over.
   *
   * Callers pass an inline arrow — `onCompleted={() => fetchEstimate(id)}` — which
   * is a NEW function on every render. With it in the dependency list, this is the
   * loop it caused on the estimate screen the moment a lookup actually succeeded:
   *
   *   poll → onCompleted → fetchEstimate → setState → re-render →
   *   new onCompleted identity → `load` changes → effect re-runs → immediate poll → …
   *
   * The screen refreshed continuously and hammered the API. It was invisible while
   * the queue held nothing but failures, because nothing ever called back. A ref
   * keeps the latest callback without making the poll depend on its identity.
   */
  const onCompletedRef = useRef(onCompleted);
  onCompletedRef.current = onCompleted;

  /** Has the first read happened? Everything already finished by then is history. */
  const seeded = useRef(false);

  const load = useCallback(async () => {
    try {
      const all = await repairDataLookupApi.list();
      // Not filtered by document yet: the job shape carries no documentId, so
      // there is nothing honest to filter ON. Showing the whole shop's queue is
      // also the more useful default — with one vendor seat, a job on someone
      // else's estimate is exactly why yours has not started.
      setJobs(all);
      setFailed(false);

      // The FIRST read is a baseline, not news.
      //
      // The queue lists the shop's last 25 jobs, most of them long finished. Firing
      // the completion callback for those on mount reloads the screen once per
      // historic job — up to 25 refetches for opening a page, and every one of them
      // announcing something that happened yesterday. Only a job that finishes while
      // this panel is watching is a completion.
      const first = !seeded.current;
      seeded.current = true;

      for (const j of all) {
        if (j.status === 'succeeded' && !announced.current.has(j.jobId)) {
          announced.current.add(j.jobId);
          if (!first) onCompletedRef.current?.(j);
        }
      }
    } catch {
      // A queue we cannot read must not take the estimate down with it. The quote
      // is still correct without this panel.
      setFailed(true);
    }
    // Deliberately EMPTY. Nothing in here depends on a prop or on state, and the
    // callback is reached through a ref — see above. A dependency here re-creates
    // the poll on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
    // Poll while anything is live. Jobs run for minutes, so this is deliberately
    // slow — a tight poll on a 15-minute job is a request storm for no new
    // information.
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  const cancel = async (job: LookupJob) => {
    const { status } = await repairDataLookupApi.cancel(job.jobId);
    // Say which of the two actually happened. "Removed from the queue" and
    // "stopping, work so far is discarded" are different facts and the person who
    // pressed it deserves the true one.
    setJobs((prev) =>
      prev.map((j) => (j.jobId === job.jobId ? { ...j, status } : j))
    );
    load();
  };

  // Which job is having a choice applied, so the buttons disable while it runs.
  // This one writes a line, so a double press is a double line.
  const [choosing, setChoosing] = useState<string | null>(null);

  /**
   * Answer "which component did you mean".
   *
   * This queues a NEW lookup down the one chosen path — nothing has been read yet,
   * so there is no cached result to build from. The server does the queueing; this
   * only sends the decision.
   */
  const chooseComponent = async (job: LookupJob, path: string[]) => {
    setChoosing(job.jobId);
    try {
      await repairDataLookupApi.choose(job.jobId, { path: path.join(' > ') } as any);
      await load();
    } finally {
      setChoosing(null);
    }
  };

  /**
   * Answer the vehicle question, or say plainly that nobody knows.
   *
   * Nothing resumes. The answer is recorded against the vehicle with its source and
   * the advisor runs the lookup again — a refused lookup restarts in well under a
   * minute, and holding the shop's only vendor seat while somebody walks out to look
   * at the truck costs more than starting over.
   *
   * `null` means SKIP, and it is sent rather than withheld. Recording nothing would
   * leave "never asked" and "asked and did not know" identical, and the question
   * would come back on every single run.
   */
  const answerVehicle = async (
    job: LookupJob,
    option: { ymm: string; engine: string } | null
  ) => {
    const ask = job.needsVehicleDetail;
    if (!ask || !job.documentType || !job.documentId) return;

    // The vendor's field name, mapped to the column the vehicle record keeps it in.
    const column =
      ask.field === 'driveType' ? 'drive_type' : ask.field === 'trim' ? 'trim' : 'engine_size';

    // For a drivetrain the ANSWER is 2WD or 4WD, not the whole card — the card is
    // what was shown, and it is sent separately as `options` so the choice can be
    // judged later against what was actually on screen.
    let value: string | null = null;
    if (option) {
      value =
        ask.field === 'driveType'
          ? (/\b(4WD|4X4|AWD)\b/i.test(option.ymm) ? '4WD' : /\b(2WD|4X2|RWD|FWD)\b/i.test(option.ymm) ? '2WD' : option.ymm)
          : ask.field === 'engine'
            ? option.engine
            : option.ymm;
    }

    setChoosing(job.jobId);
    try {
      const result = await repairDataLookupApi.recordVehicleDetail({
        documentType: job.documentType,
        documentId: job.documentId,
        field: column,
        value,
        source: option ? 'advisor_selected' : 'advisor_skipped',
        // Verbatim, so a later reader can see a real decision rather than a value.
        options: ask.options.map((o) => `${o.ymm} ${o.engine}`.trim()),
        jobId: job.jobId,
      });
      // Say what happened. Answering used to record the detail and refresh a list
      // that looked identical, so the advisor was left staring at a screen where
      // nothing visibly happened and the lookup they were mid-way through never ran.
      message.info(result.nextStep);
      await load();
    } finally {
      setChoosing(null);
    }
  };

  const choose = async (job: LookupJob, option: { qualifier: string | null; operation: string }) => {
    setChoosing(job.jobId);
    try {
      // No second scrape: the server answers from the result it already captured.
      await repairDataLookupApi.choose(job.jobId, {
        bank: option.qualifier,
        operation: option.qualifier ? null : option.operation,
      });
      await load();
    } finally {
      setChoosing(null);
    }
  };

  const live = jobs.filter((j) => !isDone(j.status));

  const body = failed ? (
    <Alert
      type="warning"
      showIcon
      message="Could not read the lookup queue"
      description="Any lookups already running are unaffected."
    />
  ) : jobs.length === 0 ? (
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No lookups queued" />
  ) : (
    <List
      size="small"
      dataSource={jobs}
      renderItem={(job) => {
        const p = job.progress;
        const hasFraction = p?.done != null && p?.total != null && p.total > 0;
        const wait = waitText(job);
        // A job with no document of its own is nobody else's to answer, so it
        // stays answerable wherever it is seen.
        const belongsElsewhere =
          job.documentId != null &&
          documentId != null &&
          (job.documentId !== documentId || job.documentType !== documentType);
        const ownerWord =
          job.documentType && job.documentId
            ? `${DOCUMENT_WORD[job.documentType]} #${job.documentId}`
            : 'another job';
        return (
          <List.Item
            actions={
              isDone(job.status)
                ? []
                : [
                    <Popconfirm
                      key="cancel"
                      title={
                        job.status === 'running'
                          ? 'Stop this lookup?'
                          : 'Remove this from the queue?'
                      }
                      description={
                        job.status === 'running'
                          ? 'It stops shortly. The work done so far is discarded.'
                          : 'It has not started, so nothing is lost.'
                      }
                      okText="Yes"
                      cancelText="No"
                      onConfirm={() => cancel(job)}
                    >
                      <Button size="small" danger type="text">
                        Cancel
                      </Button>
                    </Popconfirm>,
                  ]
            }
          >
            <List.Item.Meta
              title={
                <span>
                  <Tag color={STATUS[job.status].color}>{STATUS[job.status].label}</Tag>
                  {job.label || 'ALLDATA lookup'}
                  {belongsElsewhere && (
                    <Tag style={{ marginLeft: 8 }}>on {ownerWord}</Tag>
                  )}
                  {wait && (
                    <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                      {wait}
                    </Text>
                  )}
                </span>
              }
              description={
                <div style={{ fontSize: 12 }}>
                  {p?.text && <div>{p.text}</div>}
                  {hasFraction && (
                    <Progress
                      percent={Math.round((p!.done! / p!.total!) * 100)}
                      size="small"
                      status={job.status === 'running' ? 'active' : 'normal'}
                      format={() => `${p!.done}/${p!.total} ${p!.unit ?? ''}`.trim()}
                    />
                  )}
                  {/* WHICH COMPONENT did they mean.
                      Asked before the deep read, so answering costs the lookup it
                      was always going to cost and nothing extra. The path is shown
                      under the name because "Wheel Bearing" under Suspension and
                      "Wheel Bearing" under Axle are different jobs, and the name
                      alone cannot tell them apart. */}
                  {!belongsElsewhere && job.status === 'needs_choice' && job.needsComponentChoice && (
                    <div style={{ marginTop: 4 }}>
                      <div style={{ marginBottom: 4 }}>
                        <Text type="warning">{job.needsComponentChoice.message}</Text>
                      </div>
                      {job.needsComponentChoice.options.map((o) => (
                        <Button
                          key={o.path.join('>')}
                          size="small"
                          style={{
                            marginRight: 6,
                            marginBottom: 4,
                            height: 'auto',
                            textAlign: 'left',
                            whiteSpace: 'normal',
                          }}
                          loading={choosing === job.jobId}
                          disabled={choosing != null}
                          onClick={() => chooseComponent(job, o.path)}
                        >
                          <div>{o.component}</div>
                          <div style={{ fontSize: 10, color: '#6b7280' }}>{o.path.join(' › ')}</div>
                        </Button>
                      ))}
                    </div>
                  )}
                  {/* WHICH VEHICLE. Asked earliest of the three, before a single
                      page has been read, because the vendor publishes this truck as
                      2WD and as 4WD and they are genuinely different jobs — the 4WD
                      front hub takes a halfshaft nut the 2WD does not. The options
                      are the vendor's own wording, shown verbatim: re-describing them
                      would put a list on screen the advisor cannot check against
                      anything. */}
                  {!belongsElsewhere && job.status === 'needs_choice' && job.needsVehicleDetail && (
                    <div style={{ marginTop: 4 }}>
                      <div style={{ marginBottom: 4 }}>
                        <Text type="warning">{job.needsVehicleDetail.question}</Text>
                      </div>
                      {job.needsVehicleDetail.options.map((o) => (
                        <Button
                          key={`${o.ymm}-${o.engine}`}
                          size="small"
                          style={{
                            marginRight: 6,
                            marginBottom: 4,
                            height: 'auto',
                            textAlign: 'left',
                            whiteSpace: 'normal',
                          }}
                          loading={choosing === job.jobId}
                          disabled={choosing != null}
                          onClick={() => answerVehicle(job, o)}
                        >
                          <div>{o.ymm}</div>
                          <div style={{ fontSize: 10, color: '#6b7280' }}>{o.engine}</div>
                        </Button>
                      ))}
                      {/* Offered on purpose. Someone who cannot tell should say so
                          rather than guess, and a recorded skip stops the same
                          question being asked again on every run. */}
                      <Button
                        size="small"
                        type="text"
                        style={{ marginBottom: 4 }}
                        loading={choosing === job.jobId}
                        disabled={choosing != null}
                        onClick={() => answerVehicle(job, null)}
                      >
                        I can't tell
                      </Button>
                      <div style={{ fontSize: 10, color: '#6b7280' }}>
                        Recorded against the vehicle, then run the lookup again.
                      </div>
                    </div>
                  )}
                  {belongsElsewhere && job.status === 'needs_choice' && (
                    <div style={{ marginTop: 4 }}>
                      <Text type="warning">
                        Waiting on an answer — open {ownerWord} to give it.
                      </Text>
                    </div>
                  )}
                  {/* The VIN and the record disagree. Deliberately NOT a set of
                      buttons: one of the two sources is wrong, and picking one here
                      would write a value over a contradiction instead of resolving
                      it. Someone has to look at the vehicle and fix the record. */}
                  {job.status === 'needs_choice' && job.vehicleConflict && (
                    <div style={{ marginTop: 4 }}>
                      <Text type="warning">{job.vehicleConflict.question}</Text>
                      <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>
                        On file: {job.vehicleConflict.recorded ?? 'not set'} · From the VIN:{' '}
                        {job.vehicleConflict.decoded ?? 'not stated'}
                      </div>
                    </div>
                  )}
                  {job.status === 'needs_choice' && job.needsChoice && (
                    <div style={{ marginTop: 4 }}>
                      <div style={{ marginBottom: 4 }}>
                        <Text type="warning">{job.needsChoice.message}</Text>
                      </div>
                      {job.needsChoice.options.map((o) => (
                        <Button
                          key={`${o.operation}-${o.qualifier ?? ''}`}
                          size="small"
                          style={{ marginRight: 6, marginBottom: 4 }}
                          loading={choosing === job.jobId}
                          disabled={choosing != null}
                          onClick={() => choose(job, o)}
                        >
                          {/* ALLDATA's own hours on the button. Doing both sides is
                              NOT the sum of the two, and the advisor should be able
                              to see that rather than assume it. */}
                          {o.qualifier ?? o.operation} — {o.hours}h
                        </Button>
                      ))}
                    </div>
                  )}
                  {job.status === 'succeeded' && (
                    // What reached the quote, in one line. "Done" on its own does not
                    // distinguish a job that added a $970 line from one that added an
                    // empty one, and the empty one is the failure this flow produces
                    // quietly.
                    <Text type={job.createdLineId ? 'secondary' : 'warning'}>
                      {job.createdLineId
                        ? `Added ${typeof job.totalPrice === 'number' ? `$${job.totalPrice.toFixed(2)}` : 'a line'}` +
                          `${job.billedPartCount ? ` · ${job.billedPartCount} part${job.billedPartCount === 1 ? '' : 's'}` : ''}` +
                          `${job.suggestedPartCount ? ` · ${job.suggestedPartCount} suggested` : ''}`
                        : 'Finished, but nothing was added to the estimate.'}
                    </Text>
                  )}
                  {/*
                    THE RECEIPT.

                    A lookup reads pages on a subscription with one seat, and its only
                    trace was a line on an estimate — nothing said when it ran, how long
                    it took, or which pages it opened, so a price that looked wrong had
                    no trail to follow. Everything here already existed on the job; none
                    of it reached the screen.
                  */}
                  {isDone(job.status) && (job.startedAt || job.pathUsed) && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {job.startedAt && `Ran ${whenRan(job.startedAt)}`}
                      {typeof job.durationSeconds === 'number' &&
                        ` · took ${howLong(job.durationSeconds)}`}
                      {!!job.pathUsed?.length && ` · read ${job.pathUsed.join(' › ')}`}
                      {!!job.urlsVisited?.length &&
                        ` · ${job.urlsVisited.length} page${job.urlsVisited.length === 1 ? '' : 's'} opened`}
                    </Text>
                  )}
                  {/*
                    Every path tried, when none worked. "Found nothing" and "tried these
                    three and here is what each said" are different answers, and only the
                    second one can be acted on.
                  */}
                  {!!job.attempts?.length && !job.pathUsed && (
                    <Text type="warning" style={{ fontSize: 12 }}>
                      Tried {job.attempts.map((a) => a.path.join(' › ')).join('; ')}
                    </Text>
                  )}
                  {job.error && <Text type="danger">{job.error}</Text>}
                  {!!job.unreadable?.length && (
                    // Reported, never silently dropped — a short list that reads as
                    // complete is what strands a truck.
                    <Text type="warning">
                      Could not read {job.unreadable.length}:{' '}
                      {job.unreadable.slice(0, 3).join(', ')}
                    </Text>
                  )}
                </div>
              }
            />
          </List.Item>
        );
      }}
    />
  );

  if (bare) return body;
  return (
    <Card
      size="small"
      title={`ALLDATA lookups${live.length ? ` (${live.length} active)` : ''}`}
      style={{ marginTop: 12 }}
    >
      {body}
    </Card>
  );
};

export default LookupQueuePanel;
